import React, { useCallback, useContext, useState } from 'react';
import { Trans, useI18nContext } from '@ecg-marktplaats/js-react-i18n';

import placeBid from '../../repositories/placeBid';
import getBids from '../../repositories/getBids';

import convertToCents from '../../utils/convertToCents';
import { trackGAEvent } from '../../../../utils/gaTracking';
import EnvironmentContext from '../../../../contexts/EnvironmentContext';
import LoginDialogContainer from '../../../../components/LoginDialog/LoginDialogContainer';
import delayLoginReload from '../../../../components/LoginDialog/utils/delayLoginReload';
import BiddingForm from './BiddingForm';
import BiddingList from './BiddingList';

import { TListingClient } from 'types/TListing';
import { TBiddingClient } from 'types/TBidding';

import Classes from './Bidding.scss';
import { GA_TRACK_EVENTS_ACTIONS, GA_PARAMETER } from '../../../../utils/constants';
import createCustomDimension from '../../../../utils/createCustomDimension';
import { displayBannerAfterInteraction } from '../../../BannersApp/components/Banners/utils/banners';
import { BANNER_POSITION } from '../../../BannersApp/constants/banners';

let BiddingMessageDialog;

type TBiddingProps = {
  itemId: TListingClient['itemId'];
  bidsInfo: TListingClient['bidsInfo'];
  title: TListingClient['title'];
  sellerName: TListingClient['seller']['name'];
  sellerType: TListingClient['seller']['sellerType'];
  priceType: TListingClient['priceInfo']['priceType'];
  withPhoneNumber: boolean;
};

const Bidding = ({ itemId, title, sellerName, bidsInfo, priceType, withPhoneNumber, sellerType }: TBiddingProps) => {
  const { t } = useI18nContext();
  const {
    userLoggedIn,
    xsrfToken,
    userDetails: { name = '', isAdOwner = false, phoneNumber = '' } = {},
    hasLicensePlate,
  } = useContext(EnvironmentContext);

  const [bids, setBids] = useState<TListingClient['bidsInfo']['bids']>(bidsInfo.bids);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [currentMinimumBid] = useState<TListingClient['bidsInfo']['currentMinimumBid']>(bidsInfo.currentMinimumBid);
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [showBiddingMessageDialog, setShowBiddingMessageDialog] = useState(false);
  const [isBiddingMessageDialogLoaded, setBiddingMessageDialogLoaded] = useState(false);
  const [amount, setAmount] = useState('');
  const [formPhoneNumber, setFormPhoneNumber] = useState(phoneNumber);
  const isReserved = priceType === 'RESERVED';

  const fetchBids = useCallback(async () => {
    const response: TBiddingClient = await getBids({ itemId, xsrfToken });
    if (response.bids) {
      setBids(response.bids);

      // enable when backend issue will be fixed
      // todo: backend should return an updated value instead of 0 all the time
      // setCurrentMinimumBid(response.currentMinimumBid);
    }
  }, [itemId, xsrfToken]);

  const loadBiddingMessageDialog = async () => {
    if (!isBiddingMessageDialogLoaded) {
      try {
        BiddingMessageDialog = (await import(/* webpackChunkName: "BiddingMessageDialog" */ './BiddingMessageDialog'))
          .default;
        setBiddingMessageDialogLoaded(true);
      } catch (err) {}
    }
  };

  const submitBid = async (message) => {
    trackGAEvent('R2SBidAttempt');
    const bidAmountCents = convertToCents(amount);

    const [{ success }, { default: showToastNotification }] = await Promise.all([
      placeBid({
        itemId,
        bidAmountCents,
        message,
        phoneNumber: withPhoneNumber ? formPhoneNumber : '',
        xsrfToken,
      }),
      import(/* webpackChunkName: "toastNotification" */ '../../../../components/Toast/ToastNotification'),
      import(/* webpackChunkName: "BiddingMessageDialog" */ './BiddingMessageDialog'),
    ]);

    // success state
    if (success) {
      trackGAEvent(GA_TRACK_EVENTS_ACTIONS.R2SBidSuccess, '', {
        ...createCustomDimension({
          dimension: GA_PARAMETER.AD_SELLER_TYPE,
          payload: sellerType,
        }),
        ...createCustomDimension({
          dimension: GA_PARAMETER.AD_HAS_LICENSE_PLATE,
          payload: hasLicensePlate,
        }),
      });

      showToastNotification({
        type: 'success',
        description: t('bidding.notifications.placed'),
      });

      displayBannerAfterInteraction(BANNER_POSITION.STATIC.ACTION);

      if (!userLoggedIn) {
        await delayLoginReload();
        document.location.reload();
      } else {
        await fetchBids();
      }
    } else {
      showToastNotification({
        type: 'error',
        description: t('error.generic'),
      });
    }
  };

  const handleConfirm = (message: string) => {
    setShowBiddingMessageDialog(false);
    submitBid(message);
    setAmount('');
  };

  const handleCancel = () => {
    trackGAEvent('R2SBidCancel');
    setShowBiddingMessageDialog(false);
  };

  const handleSubmit = () => {
    if (userLoggedIn) {
      trackGAEvent('R2SBidBegin');
      loadBiddingMessageDialog();
      setShowBiddingMessageDialog(true);
    } else {
      setShowLoginDialog(true);
    }
  };

  const handleLoginSuccess = () => {
    trackGAEvent('R2SBidBegin');
    loadBiddingMessageDialog();
    setShowBiddingMessageDialog(true);
  };

  return (
    <div className={`block-wrapper block-wrapper-s--hide-top ${Classes.root}`}>
      {!isReserved ? (
        <>
          <Trans className={Classes.title} tagName="h3" i18nKey="bidding.title" />
          {!isAdOwner && (
            <BiddingForm
              currentMinimumBid={currentMinimumBid}
              onSubmit={handleSubmit}
              amount={amount}
              setAmount={setAmount}
              setPhoneNumber={setFormPhoneNumber}
              withPhoneNumber={withPhoneNumber}
              phoneNumber={formPhoneNumber}
            />
          )}
        </>
      ) : (
        <>
          <Trans className={Classes.title} tagName="h3" i18nKey="bidding.reserved.title" />
          <Trans className={Classes.description} tagName="p" i18nKey="bidding.reserved.description" />
        </>
      )}
      <BiddingList bids={bids} isListingReserved={isReserved} onRemove={fetchBids} />

      {isBiddingMessageDialogLoaded && showBiddingMessageDialog && (
        <BiddingMessageDialog
          amount={convertToCents(amount)}
          listingTitle={title}
          sellerName={sellerName}
          userName={name}
          onCancel={handleCancel}
          onConfirm={handleConfirm}
        />
      )}

      {showLoginDialog && <LoginDialogContainer onClose={setShowLoginDialog} onSuccess={handleLoginSuccess} />}
    </div>
  );
};

export default Bidding;
